import Image from "next/image";
import Link from "next/link";
import { ReactNode } from "react";

import { CardWithHint } from "app/seo/modes-de-livraison/CardWithHint";
import BreadCrumb from "shared/BreadCrumb";
import { AppLayout } from "shared/Layout";
import { fetchSettings } from "shared/SettingsProvider/settings.helper";
import { Button } from "ui/Button";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { fetchSEO, withSeo } from "utils/with-seo.utils";

import { Banner } from "../Banner";

import DeliveryTruck from "./delivery-truck.svg";
import QuestionMark from "./question-mark.svg";
import RelayPoint from "./relay-point.svg";

export type ModesDeLivraisonProps = Awaited<
  ReturnType<typeof getStaticProps>
>["props"];

export const ModesDeLivraison = () => {
  const i18n = useI18n();

  return (
    <>
      <div className="mx-auto max-w-[1440px]">
        <BreadCrumb
          parts={[
            { label: i18n.t("breadcrumb.home"), to: "/" },
            {
              label: i18n.t("breadcrumb.shippingMethods"),
              to: "/modes-de-livraison",
            },
          ]}
        />
        <div className="flex flex-col px-[1.875rem] xl:px-[7.5rem]">
          <Banner firstLine={i18n.t("shippingMethods.title")} />
          <div className="flex flex-col gap-10">
            <section className="py-5 px-4 text-lg leading-6 text-center bg-yellow rounded-xl md:px-20 md:text-2xl">
              {i18n.t("shippingMethods.description", {}, { html: true })}
            </section>
            <div className="flex flex-col gap-2 md:flex-row">
              <CardWithHint
                title={i18n.t(
                  "shippingMethods.express.title",
                  {},
                  { html: true },
                )}
                description={i18n.t(
                  "shippingMethods.express.description",
                  {},
                  { html: true },
                )}
                hint={i18n.t(
                  "shippingMethods.express.hint",
                  {},
                  { html: true },
                )}
                icon={<DeliveryTruck />}
              />
              <CardWithHint
                title={i18n.t(
                  "shippingMethods.relayPoint.title",
                  {},
                  { html: true },
                )}
                description={i18n.t(
                  "shippingMethods.relayPoint.description",
                  {},
                  { html: true },
                )}
                hint={i18n.t(
                  "shippingMethods.relayPoint.hint",
                  {},
                  { html: true },
                )}
                icon={<RelayPoint />}
              />
            </div>
            <Link href={"/smartphone"}>
              <a className="self-center">
                <Button className="self-center w-72">
                  {i18n.t("shippingMethods.CTA.title")}
                </Button>
              </a>
            </Link>
            <div className="flex flex-col gap-1">
              <h2 className="self-center text-2xl font-bold leading-9 md:text-3xl md:leading-[3.875rem]">
                {i18n.t("shippingMethods.trackOrder.title")}
              </h2>
              <p className="self-center text-base leading-7 text-center md:text-lg md:leading-7 lg:text-left">
                {i18n.t("shippingMethods.trackOrder.customerSpace")}
                <Link href={"/espace-membre"}>
                  <a className="text-cornflower-blue whitespace-nowrap">
                    www.mobile.club/espace-membre.
                  </a>
                </Link>
              </p>
            </div>
            <div className="flex flex-col gap-6 md:flex-row md:gap-3">
              <div className="basis-1/2">
                <div className="relative h-[240px] sm:h-[341px]">
                  <Image
                    layout="fill"
                    objectFit="contain"
                    src="/images/seo/modes-de-livraison/shipping-status-example.webp"
                    alt="Exemple de suivis de livraison"
                  />
                </div>
              </div>
              <div className="flex basis-1/2 flex-col gap-4 text-base leading-[1.625rem] md:text-lg">
                <p>
                  {i18n.t(
                    "shippingMethods.trackOrder.applicationInProgress",
                    {},
                    { html: true },
                  )}
                </p>
                <p>
                  {i18n.t(
                    "shippingMethods.trackOrder.applicationIncomplete",
                    {},
                    { html: true },
                  )}
                </p>
                <p>
                  {i18n.t(
                    "shippingMethods.trackOrder.applicationRefused",
                    {},
                    { html: true },
                  )}
                </p>
                <p>
                  {i18n.t(
                    "shippingMethods.trackOrder.applicationAccepted",
                    {},
                    { html: true },
                  )}
                </p>
                <p>
                  {i18n.t(
                    "shippingMethods.trackOrder.orderSent",
                    {},
                    { html: true },
                  )}
                </p>
                <p>
                  {i18n.t(
                    "shippingMethods.trackOrder.orderReceived",
                    {},
                    { html: true },
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-row gap-4 justify-center items-center py-2.5 px-6 mb-[70px] text-base text-white bg-cornflower-blue rounded-xl md:gap-7 md:text-lg md:leading-6">
              <div className="flex basis-1/3 justify-center md:basis-auto">
                <QuestionMark />
              </div>

              {i18n.t("shippingMethods.customerService", {}, { html: true })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ModesDeLivraison.getLayout = (
  page: ReactNode,
  pageProps: ModesDeLivraisonProps,
) => (
  <AppLayout withBanner {...pageProps}>
    {page}
  </AppLayout>
);

export const getStaticProps = async () => {
  const translations = await fetchTranslations([
    "marquee",
    "breadcrumb",
    "shipping-methods",
  ]);

  const seo = await fetchSEO("modes-de-livraison");

  return {
    props: {
      translations,
      seo,
      settings: await fetchSettings(),
    },
  };
};

export default withApp(ModesDeLivraison, [withSeo]);
